import { rebuildNotificationsACL } from '@/services/NotificationServices'
import { useMutation } from '@tanstack/react-query'

import { NotificationsAPI, TeamsAPI, UsersAPI } from '@makyoapp/makyo-sdk'
import { LANGUAGE_VALUES, NotificationSettingsAdapter } from '@makyoapp/makyo-common'

import { mutationKeys } from '@/lib/react-query/mutation-keys'
import { useMyNotificationsContext } from '@/context/MyNotificationsContext'
import { useNotificationsContext } from '@/context/NotificationsContext'
import useAuth from '@/hooks/useAuth'
import { useReqError } from '@/hooks/useReqError'

/**
 * useAddTeamMutation is a custom hook for managing the addition of a team to the user's organization.
 * It uses the TeamsAPI to add a new team.
 *
 * @returns {function} A mutation function that triggers a team addition operation.
 * The function should be called with an object with the following properties:
 *    - newTeam: An object representing the new team that should be added. It should have the following properties:
 *      - name: The name of the new team
 *      - avatar: The avatar of the new team (or null if no avatar is provided)
 *      - orgId: The ID of the organization that the team should be added to
 */
export const useAddTeamMutation = () => {
  return useMutation({
    mutationKey: [mutationKeys.user.addTeam],
    mutationFn: ({ newTeam }: { newTeam: any }) =>
      TeamsAPI.ReqAdd({
        _Name: newTeam.name,
        _Avatar: newTeam.avatar || null,
        _OrganizationID: newTeam.orgId,
        _Members: [],
        _Description: '',
      }),
  })
}

/**
 * useUpdateReferralMutation is a custom hook for managing updates to the user's referral code.
 * It uses the UsersAPI to update the referral code.
 *
 * @returns {function} A mutation function that triggers a referral code update operation.
 * The function should be called with an object with the following properties:
 *    - value: The new referral code
 */
export const useUpdateReferralMutation = () =>
  useMutation({
    mutationKey: [mutationKeys.user.updateReferral],
    mutationFn: ({ value }: { value: string }) =>
      UsersAPI.ReqUpdateReferral({
        _ReferalCode: value,
      }),
  })

/**
 * useDeleteProfileMutation is a custom hook for managing the deletion of the user's profile.
 * It uses the UsersAPI to delete the user's profile.
 *
 * @returns {function} A mutation function that triggers a profile deletion operation.
 */
export const useDeleteProfileMutation = () =>
  useMutation({
    mutationKey: [mutationKeys.user.deleteProfile],
    mutationFn: () => UsersAPI.ReqDelete(),
  })

/**
 * useUpdateAclMutation is a custom hook for managing updates to the user's Access Control List (ACL).
 * It uses the UsersAPI to update the user's ACL and also updates the ACL in local state.
 *
 * @returns {function} A mutation function that triggers an ACL update operation.
 * The function should be called with an object with the following properties:
 *    - nextValues: An array of adapters representing the new ACL settings
 */
export const useUpdateAclMutation = () => {
  const { userData, updateNotifications } = useAuth()

  return useMutation({
    mutationKey: [mutationKeys.user.updateUserAcl],
    mutationFn: ({ nextValues }: { nextValues: NotificationSettingsAdapter[] }) => {
      if (userData?.notifications) {
        const notificationACL = rebuildNotificationsACL(nextValues, userData?.notifications)

        return UsersAPI.ReqUpdateNotificationSettings(notificationACL).then(() => {
          updateNotifications(notificationACL)
        })
      }

      return Promise.reject('User Data Notification is Empty')
    },
  })
}

type TUpdateProflePayload = {
  newName?: string
  newTheme?: 'dark' | 'light'
  newLang: LANGUAGE_VALUES
  newTz: string
  newLocale: string
}

/**
 * useUpdateProfileMutation is a custom hook for managing user profile updates.
 * It uses the UsersAPI to update the user profile and also updates the profile in local state.
 *
 * @returns {function} A mutation function that triggers a profile update operation.
 * The function should be called with an object with the following properties:
 *    - newName: The new name for the user
 *    - newTheme: The new theme for the user
 *    - newLang: The new language for the user
 *    - newTz: The new timezone for the user
 *    - newLocale: The new locale for the user
 */
export const useUpdateProfileMutation = () => {
  const { updateProfile } = useAuth()

  return useMutation({
    mutationKey: [mutationKeys.user.updateProfile],
    mutationFn: ({ newName, newTheme, newLang, newTz, newLocale }: TUpdateProflePayload) =>
      UsersAPI.ReqUpdateProfile({
        _Name: newName || '',
        _DarkTheme: newTheme === 'dark',
        _Language: newLang,
        _TimeZone: newTz,
        _Locale: newLocale,
      }).then(() => {
        // If the request is successful, update the profile in local state and show a success message
        updateProfile({
          name: newName,
          theme: newTheme,
          lang: newLang as any,
          timezone: newTz,
          locale: newLocale,
        })
      }),
  })
}

/**
 * useReadNotifMutation is a custom hook for managing read notifications.
 * It uses the NotificationsAPI to mark notifications as read.
 * It also manages the loading state and error handling for the operation.
 *
 * @returns {function} A mutation function that triggers a read operation.
 */
export const useReadNotifMutation = () => {
  const { updateRead, setUnreadCount, unreadCount } = useNotificationsContext()
  const { checks, notifications, setNotifications, setChecks, setIsLoading, page } = useMyNotificationsContext()
  const printAPIError = useReqError()

  const tobeUpdatedNotifs = checks.filter((notif) => !notif.isRead)
  const notifs = tobeUpdatedNotifs.map((notif) => notif.id)

  return useMutation({
    mutationKey: [mutationKeys.user.readNotification],
    mutationFn: () => {
      setIsLoading(true)
      return NotificationsAPI.ReqRead({
        _Notifs: notifs,
      })
        .then(() => {
          // On success, clear the checks
          setChecks([])

          // // If we are on the first page, update the read count and unread count
          if (page === 1) {
            const [readCount] = updateRead(notifs)
            setUnreadCount(unreadCount - readCount)
          }

          // Make a copy of the current notifications
          const copyNt = [...notifications.items]

          // Array to store the indexes of the updated notifications
          const indexes = []

          // Loop over the notifications to be updated
          for (const nt of tobeUpdatedNotifs) {
            // Find the index of the current notification in the copy
            const idx = copyNt.findIndex((n) => n.id === nt.id)

            // If found, set its `isRead` property to true and store its index
            if (idx !== -1) {
              indexes.push(idx)
              copyNt[idx].isRead = true
            }
          }

          // Set the new notifications state with the updated notifications
          setNotifications({
            items: copyNt,
            totalCount: notifications.totalCount,
          })
        })
        .catch((e) => {
          printAPIError(e)
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
  })
}

/**
 * useUnreadNotifMutation is a custom hook for managing unread notifications.
 * It uses the NotificationsAPI to mark notifications as unread.
 * It also manages the loading state and error handling for the operation.
 *
 * @returns {function} A mutation function that triggers an unread operation.
 */
export const useUnreadNotifMutation = () => {
  const { updateUnread, setUnreadCount, unreadCount } = useNotificationsContext()
  const { checks, notifications, setNotifications, setChecks, setIsLoading, page } = useMyNotificationsContext()
  const printAPIError = useReqError()
  const tobeUpdatedNotifs = checks.filter((notif) => notif.isRead)

  // Map the filtered notifications to an array of their IDs
  const notifs = tobeUpdatedNotifs.map((notif) => notif.id)

  // Call the API to mark these notifications as unread
  return useMutation({
    mutationKey: [mutationKeys.user.unreadNotification],
    mutationFn: () => {
      setIsLoading(true)
      return NotificationsAPI.ReqUnread({
        _Notifs: notifs,
      })
        .then(() => {
          // On success, clear the checks
          setChecks([])

          // If we are on the first page, update the unread count
          if (page === 1) {
            const [upUnreadCount] = updateUnread(notifs)
            setUnreadCount(unreadCount + upUnreadCount)
          }

          // Make a copy of the current notifications
          const copyNt = [...notifications.items]

          // Array to store the indexes of the updated notifications
          const indexes = []

          // Loop over the notifications to be updated
          for (const nt of tobeUpdatedNotifs) {
            // Find the index of the current notification in the copy
            const idx = copyNt.findIndex((n) => n.id === nt.id)

            // If found, set its `isRead` property to false and store its index
            if (idx !== -1) {
              indexes.push(idx)
              copyNt[idx].isRead = false
            }
          }

          // Set the new notifications state with the updated notifications
          setNotifications({
            items: copyNt,
            totalCount: notifications.totalCount,
          })
        })
        .catch((e) => {
          printAPIError(e)
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
  })
}

/**
 * useDeleteNotifMutation is a custom hook for managing deletion of notifications.
 * It uses the NotificationsAPI to delete the notifications.
 * It also manages the loading state and error handling for the deletion operation.
 *
 * @returns {function} A mutation function that triggers a delete operation.
 */
export const useDeleteNotifMutation = () => {
  useNotificationsContext()
  const { checks, handleList, setIsLoading, setChecks } = useMyNotificationsContext()
  const printAPIError = useReqError()
  return useMutation({
    mutationKey: [mutationKeys.user.deleteNotification],
    mutationFn: () => {
      setIsLoading(true)
      return NotificationsAPI.ReqDelete({
        // Map the checks to an array of their IDs
        _Notifs: checks.map((notifs) => notifs.id),
      })
        .then(() => {
          // On success, refresh the list of notifications
          handleList()
          setChecks([])
        })
        .catch((e) => {
          printAPIError(e)
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
  })
}
